import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { getBibleSearchAction } from "../../store/Bible/BibleAction";

import { LuSearch } from "react-icons/lu";
import CloseIcon from "../../assets/icons/Cross.svg";
import PropTypes from "prop-types";

const BibleSearch = ({ leftAlign = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { versionDetails } = useSelector((state) => state.bible);
  const { currentLanguageTitles } = useSelector(
    (state) => state.languageReducer.data
  );

  const inputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("Jesus");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchMessage, setSearchMessage] = useState("");

  const handleSearch = (search) => {
    setLoading(true);
    if (search) {
      dispatch(
        getBibleSearchAction({
          apiPayloadRequest: {
            filesetid: versionDetails?.abbr,
            question: search,
          },
          callback: (res) => {
            setSearchResults(res?.data ?? []);
            setSearchMessage(res?.message ?? "");
            setLoading(false);
          },
        })
      );
    } else {
      setSearchResults([]);
      setSearchMessage("");
      setLoading(false);
    }
  };

  useEffect(() => {
    const eventHandler = (e) => {
      let preferredLanguageDrawer = document.querySelector("#bibleSearch");
      if (
        preferredLanguageDrawer &&
        !preferredLanguageDrawer?.contains(e.target)
      ) {
        setIsSearching((preState) => !preState);
      }
    };
    document.addEventListener("mouseup", eventHandler);

    return () => document.removeEventListener("mouseup", eventHandler);
  }, []);

  useEffect(() => {
    if (searchText) handleSearch(searchText);
  }, []);

  return (
    <div>
      <div
        className={`flex w-fit cursor-pointer items-center gap-3 rounded-md bg-greyishV2 px-3 py-2 md:w-[20vw]`}
        onClick={() => {
          setIsSearching(true);
          setTimeout(() => {
            if (inputRef.current) inputRef.current.focus();
          }, 500);
        }}
      >
        <LuSearch className="text-2xl text-yellowish" />
        <p className="hidden w-full whitespace-nowrap bg-greyishV2 text-greyish md:flex">
          {currentLanguageTitles?.[0]?.bible?.bible_search_placeholder ??
            "Search the Bible"}
        </p>
      </div>
      {isSearching && (
        <div
          id="bibleSearch"
          className={`custom-scrollbar absolute top-16 z-50 flex h-[65vh] w-full flex-col overflow-y-auto rounded-md bg-greyishV2/80 p-5 text-white backdrop-blur-sm md:top-24 md:w-9/12 ${
            leftAlign ? "" : "-translate-x-1/4"
          }`}
        >
          <div className="flex items-center gap-5 pb-7">
            <div
              className={`flex w-full items-center justify-between gap-3 rounded-md bg-greyish px-3 py-2 md:min-w-[400px]`}
            >
              <input
                type="text"
                ref={inputRef}
                value={searchText}
                placeholder={
                  currentLanguageTitles?.[0]?.bible?.bible_search_placeholder ??
                  "Search the Bible"
                }
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch(searchText);
                  }
                }}
                className="w-full bg-greyish text-lg text-blackish outline-none placeholder:text-blackish"
              />
              <button
                className="flex aspect-square items-center justify-center rounded-full bg-yellowish p-1"
                onClick={() => handleSearch(searchText)}
              >
                <LuSearch className="text-xl text-blackish" />
              </button>
            </div>
            <img
              src={CloseIcon}
              alt="CloseIcon"
              className="h-5 cursor-pointer"
              onClick={() => {
                setIsSearching(false);
                setSearchText("");
              }}
            />
          </div>
          <ul className="custom-scrollbar flex flex-1 flex-col gap-1 overflow-y-auto pr-2">
            {loading ? (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-2">
                  <div className="h-6 w-full animate-pulse rounded-2xl bg-zinc-600"></div>
                  <div className="h-4 w-2/12 animate-pulse rounded-2xl bg-zinc-600"></div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="h-6 w-full animate-pulse rounded-2xl bg-zinc-600"></div>
                  <div className="h-4 w-2/12 animate-pulse rounded-2xl bg-zinc-600"></div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="h-6 w-full animate-pulse rounded-2xl bg-zinc-600"></div>
                  <div className="h-4 w-2/12 animate-pulse rounded-2xl bg-zinc-600"></div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="h-6 w-full animate-pulse rounded-2xl bg-zinc-600"></div>
                  <div className="h-4 w-2/12 animate-pulse rounded-2xl bg-zinc-600"></div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="h-6 w-full animate-pulse rounded-2xl bg-zinc-600"></div>
                  <div className="h-4 w-2/12 animate-pulse rounded-2xl bg-zinc-600"></div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="h-6 w-full animate-pulse rounded-2xl bg-zinc-600"></div>
                  <div className="h-4 w-2/12 animate-pulse rounded-2xl bg-zinc-600"></div>
                </div>
              </div>
            ) : (
              searchResults?.map((item, index) => {
                return (
                  <div
                    key={`search-item${index}`}
                    className="cursor-pointer rounded-md px-2 py-2 hover:bg-black/30"
                    onClick={() => {
                      navigate(
                        `/chapter/${versionDetails?.abbr}/${item?.book_id}/${item?.chapter}`
                      );
                      setIsSearching(false);
                    }}
                  >
                    <p className="">{item?.verse_text}</p>
                    <div className="flex w-full items-center justify-between text-sm text-greyishV3">
                      <p className="">
                        {item?.book_name}{" "}
                        <span className="">
                          {item?.chapter}:{item?.verse_start}
                        </span>
                      </p>
                    </div>
                  </div>
                );
              })
            )}

            {!loading && searchResults?.length === 0 && (
              <li className="flex cursor-pointer flex-col items-center justify-center gap-2 py-10 text-yellowish">
                <p>
                  {searchMessage === "Invalid verse reference. Please check your entry and try again."
                    ? searchMessage
                    : currentLanguageTitles?.[0]?.bible?.no_results_lbl ?? "No Results Found"}
                </p>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

BibleSearch.propTyp = {
  leftAlign: PropTypes.bool,
};

export default BibleSearch;
